import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import FileDropzone from '../../shared/components/FileDropzone';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import { money } from '../../shared/formatters';
import { openProtectedFile } from '../../lib/pb';

const STATUSES = ['active', 'discontinued', 'obsolete'];

const EMPTY_FORM = {
  part_number: '', name: '', description: '', category: '', manufacturer: '',
  supplier: '', cost_price: '', sell_price: '', stock_quantity: '',
  reorder_level: '', unit: 'each', location: '', specifications: '',
  status: 'active',
};

function formToPayload(form) {
  let specifications = {};
  if (form.specifications.trim()) {
    specifications = JSON.parse(form.specifications.trim());
  }
  return {
    part_number: form.part_number.trim(),
    name: form.name.trim(),
    description: form.description.trim(),
    category: form.category.trim(),
    manufacturer: form.manufacturer.trim(),
    supplier: form.supplier.trim(),
    cost_price: Number(form.cost_price) || 0,
    sell_price: Number(form.sell_price) || 0,
    stock_quantity: Number(form.stock_quantity) || 0,
    reorder_level: Number(form.reorder_level) || 0,
    unit: form.unit.trim() || 'each',
    location: form.location.trim(),
    specifications,
    status: form.status,
  };
}

// Admin CRUD for parts_db (update_tasks.md §7.5). No van-stock view in
// this pass (TBC-7); no auto-deduct (Track 2.1, deferred). parts_db image
// /datasheet files are not protected today (TBC-3) — protection is on the
// Phase 2 migration list; downloads still mint fresh token URLs.
export default function Parts() {
  const { data, add, update, remove } = useData();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [editing, setEditing] = useState(null); // part id or 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [datasheet, setDatasheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const parts = useMemo(() => data.parts.filter((part) => {
    if (status !== 'all' && part.status !== status) return false;
    if (category !== 'all' && part.category !== category) return false;
    if (!query) return true;
    const haystack = `${part.part_number} ${part.name} ${part.manufacturer} ${part.category} ${part.supplier}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }), [data.parts, query, category, status]);

  const categories = useMemo(() => Array.from(new Set((data.parts || []).map((part) => part.category).filter(Boolean))), [data.parts]);

  const openNew = () => { setForm(EMPTY_FORM); setImage(null); setDatasheet(null); setEditing('new'); };

  const openEdit = (part) => {
    setForm({
      part_number: part.part_number, name: part.name, description: part.description,
      category: part.category, manufacturer: part.manufacturer, supplier: part.supplier,
      cost_price: String(part.cost_price || ''), sell_price: String(part.sell_price || ''),
      stock_quantity: String(part.stock_quantity || ''), reorder_level: String(part.reorder_level || ''),
      unit: part.unit || 'each', location: part.location,
      specifications: part.specifications && Object.keys(part.specifications).length ? JSON.stringify(part.specifications, null, 2) : '',
      status: part.status || 'active',
    });
    setImage(null);
    setDatasheet(null);
    setEditing(part.id);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.part_number.trim()) { notify('Part number is required', 'error'); return; }
    if (!form.name.trim()) { notify('Part name is required', 'error'); return; }
    if (Number(form.cost_price) < 0 || Number(form.sell_price) < 0) { notify('Prices cannot be negative', 'error'); return; }
    let payload;
    try {
      payload = formToPayload(form);
    } catch {
      notify('Specifications must be valid JSON', 'error');
      return;
    }
    setBusy(true);
    try {
      if (editing === 'new') {
        await add('parts', { ...payload, image, datasheet });
        notify('Part created');
      } else {
        await update('parts', editing, { ...payload, image, datasheet });
        notify('Part saved');
      }
      setEditing(null);
    } catch (err) {
      notify(err.message || 'Could not save the part', 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await remove('parts', deleting.id);
      notify('Part deleted');
    } catch (err) {
      notify(err.message || 'Could not delete the part', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Parts Database</h1>
          <p className="field-hint">
            {parts.length} part{parts.length === 1 ? '' : 's'} — engineers see changes immediately.
          </p>
        </div>
        <div className="header-actions">
          <button className="primary-button" onClick={openNew}><SafeIcon icon={Icons.Plus} /> New part</button>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search number, name or manufacturer…" />
        </label>
        {categories.length > 0 && (
          <select value={category} onChange={(event) => setCategory(event.target.value)} className="engineer-filter" aria-label="Filter by category">
            <option value="all">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        )}
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="engineer-filter" aria-label="Filter by status">
          <option value="all">All statuses</option>
          {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>

      {!data.parts.length ? (
        <EmptyState title="No parts yet" message="Add your stock so engineers can look up part numbers and pricing." />
      ) : !parts.length ? (
        <EmptyState title="No matching parts" message="Try different filters or search." />
      ) : (
        <div className="parts-grid">
          {parts.map((part) => (
            <article className="part-card" key={part.id}>
              <div className="card-top">
                <span className="eyebrow">{part.part_number}</span>
                <span className={`stock-badge ${part.stock_quantity <= part.reorder_level ? 'low-stock' : 'in-stock'}`}>
                  {part.stock_quantity <= part.reorder_level ? 'LOW' : 'In stock'}
                </span>
              </div>
              <h3>{part.name}</h3>
              <p className="field-hint">{part.manufacturer}{part.category ? ` · ${part.category}` : ''}</p>
              <p className="part-meta">
                {money(part.sell_price)}{part.unit ? ` / ${part.unit}` : ''} · stock {part.stock_quantity}
                {part.reorder_level ? ` (reorder at ${part.reorder_level})` : ''}
              </p>
              <div className="form-actions">
                {part.datasheet && (
                  <button className="link-button" onClick={() => openProtectedFile({ id: part.id, collectionName: 'parts_db' }, part.datasheet)}>Datasheet</button>
                )}
                <button className="link-button" onClick={() => openEdit(part)}>Edit</button>
                <button className="link-button" onClick={() => setDeleting(part)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <SlideOver open={editing !== null} title={editing === 'new' ? 'New part' : 'Edit part'} onClose={() => setEditing(null)}>
        <form className="drawer-form" onSubmit={save}>
          <div className="form-row">
            <label>Part number<input value={form.part_number} onChange={(event) => setForm((f) => ({ ...f, part_number: event.target.value }))} placeholder="e.g. 7716051388" required /></label>
            <label>Name<input value={form.name} onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} placeholder="e.g. Zone valve 22mm" required /></label>
          </div>
          <label>Description<textarea rows="2" value={form.description} onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))} /></label>
          <div className="form-row">
            <label>Category<input value={form.category} onChange={(event) => setForm((f) => ({ ...f, category: event.target.value }))} placeholder="e.g. Valves" /></label>
            <label>Manufacturer<input value={form.manufacturer} onChange={(event) => setForm((f) => ({ ...f, manufacturer: event.target.value }))} placeholder="e.g. Honeywell" /></label>
          </div>
          <div className="form-row">
            <label>Supplier<input value={form.supplier} onChange={(event) => setForm((f) => ({ ...f, supplier: event.target.value }))} placeholder="e.g. City Plumbing" /></label>
            <label>Unit<input value={form.unit} onChange={(event) => setForm((f) => ({ ...f, unit: event.target.value }))} placeholder="each" /></label>
          </div>
          <div className="form-row">
            <label>Cost price<input type="number" min="0" step="0.01" value={form.cost_price} onChange={(event) => setForm((f) => ({ ...f, cost_price: event.target.value }))} /></label>
            <label>Sell price<input type="number" min="0" step="0.01" value={form.sell_price} onChange={(event) => setForm((f) => ({ ...f, sell_price: event.target.value }))} /></label>
          </div>
          <div className="form-row">
            <label>Stock quantity<input type="number" min="0" value={form.stock_quantity} onChange={(event) => setForm((f) => ({ ...f, stock_quantity: event.target.value }))} /></label>
            <label>Reorder level<input type="number" min="0" value={form.reorder_level} onChange={(event) => setForm((f) => ({ ...f, reorder_level: event.target.value }))} /></label>
          </div>
          <label>Location<input value={form.location} onChange={(event) => setForm((f) => ({ ...f, location: event.target.value }))} placeholder="e.g. Van 2, shelf C" /></label>
          <label>Specifications (JSON)
            <textarea rows="4" value={form.specifications} onChange={(event) => setForm((f) => ({ ...f, specifications: event.target.value }))} placeholder='{"voltage": "230V", "connections": "22mm"}' />
          </label>
          <label>Status
            <select value={form.status} onChange={(event) => setForm((f) => ({ ...f, status: event.target.value }))}>
              {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <FileDropzone
            onFiles={(files) => setImage(files[0] || null)}
            accept="image/jpeg,image/png,image/webp"
            multiple={false}
            hint="Product photo — JPEG/PNG/WebP up to 5 MB"
            onReject={(msgs) => notify(msgs.join('. '), 'error')}
          />
          <FileDropzone
            onFiles={(files) => setDatasheet(files[0] || null)}
            accept="application/pdf"
            multiple={false}
            hint="Datasheet PDF — up to 10 MB"
            onReject={(msgs) => notify(msgs.join('. '), 'error')}
          />
          <div className="drawer-actions">
            <button type="button" className="secondary-button" onClick={() => setEditing(null)}>Cancel</button>
            <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : editing === 'new' ? 'Create part' : 'Save changes'}</button>
          </div>
        </form>
      </SlideOver>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete part?"
        message={deleting ? `"${deleting.name}" (${deleting.part_number}) will be removed.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
